import { useState, useCallback, useRef, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Tab,
  Tabs
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import SpeedIcon from "@mui/icons-material/Speed";
import DownloadIcon from "@mui/icons-material/Download";
import StopIcon from "@mui/icons-material/Stop";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinearProgress from "@mui/material/LinearProgress";
import { PageWrapper } from "../../common-ui/PageWrapper";
import { API, Auth } from "aws-amplify";
import { Link as RouterLink } from "react-router-dom";

type ProgramOption = {
  id: string;
  name: string;
  institutionName?: string;
};

type MemberOption = {
  userId: string;
  fullName: string;
  email: string;
  roleName: string;
  membershipId: string;
};

type RunResult = {
  data: any;
  timing: number;
  eventCount: number;
  hash: string;
  timestamp: string;
};

type BenchmarkRun = {
  iteration: number;
  timing: number;
  eventCount: number;
  hash: string;
  error?: string;
};

type BenchmarkResult = {
  runs: BenchmarkRun[];
  stats: {
    count: number;
    successCount: number;
    min: number;
    max: number;
    mean: number;
    median: number;
    p95: number;
    stdDev: number;
    allHashesMatch: boolean;
  };
  params: {
    user: string;
    userId: string;
    program: string;
    mode: string;
    iterations: number;
    timestamp: string;
  };
};

function computeStats(timings: number[]) {
  if (timings.length === 0)
    return { min: 0, max: 0, mean: 0, median: 0, p95: 0, stdDev: 0 };
  const sorted = [...timings].sort((a, b) => a - b);
  const sum = sorted.reduce((a, b) => a + b, 0);
  const mean = sum / sorted.length;
  const median =
    sorted.length % 2 === 0
      ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
      : sorted[Math.floor(sorted.length / 2)];
  const p95Index = Math.min(
    Math.ceil(sorted.length * 0.95) - 1,
    sorted.length - 1
  );
  const variance =
    sorted.reduce((acc, v) => acc + (v - mean) ** 2, 0) / sorted.length;
  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    mean: Math.round(mean),
    median: Math.round(median),
    p95: sorted[p95Index],
    stdDev: Math.round(Math.sqrt(variance))
  };
}

async function getCognitoJWT(): Promise<string> {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
}

function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash + char) | 0;
  }
  return Math.abs(hash).toString(16).padStart(8, "0");
}

// ── EXPLAIN QUERY PLAN types & explorer component ──

type PlanRow = { id: number; parent: number; detail: string };

type ExplainRow = {
  addr: number;
  opcode: string;
  p1: number;
  p2: number;
  p3: number;
  p4: string;
  p5: string;
  comment: string;
};

type PlanTreeNode = PlanRow & { children: PlanTreeNode[]; depth: number };

type OperationType =
  | "scan"
  | "search_covering"
  | "search_index"
  | "auto_index"
  | "materialize"
  | "coroutine"
  | "temp_btree"
  | "compound"
  | "other";

function classifyOperation(detail: string): OperationType {
  const d = detail.toUpperCase();
  if (d.includes("AUTOMATIC COVERING INDEX")) return "auto_index";
  if (d.includes("USING COVERING INDEX")) return "search_covering";
  if (d.includes("USING INDEX")) return "search_index";
  if (d.startsWith("SCAN")) return "scan";
  if (d.startsWith("SEARCH")) return "search_index";
  if (d.includes("MATERIALIZE")) return "materialize";
  if (d.includes("CO-ROUTINE")) return "coroutine";
  if (d.includes("USE TEMP B-TREE")) return "temp_btree";
  if (d.includes("COMPOUND")) return "compound";
  return "other";
}

const OP_STYLES: Record<
  OperationType,
  { color: string; bg: string; label: string; tip: string }
> = {
  scan: {
    color: "#b71c1c",
    bg: "#ffebee",
    label: "SCAN",
    tip: "Full table scan with no index. This reads every row in the table. Consider adding an index on the filtered or joined columns."
  },
  search_index: {
    color: "#2e7d32",
    bg: "#e8f5e9",
    label: "INDEX",
    tip: "Uses an index to look up a subset of rows. Efficient for selective queries."
  },
  search_covering: {
    color: "#1b5e20",
    bg: "#c8e6c9",
    label: "COVERING",
    tip: "Uses a covering index that contains all needed columns. Best-case scenario — no table lookup required."
  },
  auto_index: {
    color: "#e65100",
    bg: "#fff3e0",
    label: "AUTO INDEX",
    tip: "SQLite created a temporary index on the fly. This adds overhead per query. Consider adding a persistent composite index."
  },
  materialize: {
    color: "#1565c0",
    bg: "#e3f2fd",
    label: "MATERIALIZE",
    tip: "A CTE or subquery is materialized into a temporary table. Normal for WITH clauses."
  },
  coroutine: {
    color: "#42a5f5",
    bg: "#e3f2fd",
    label: "CO-ROUTINE",
    tip: "A CTE evaluated lazily as a coroutine instead of being materialized. Can be more efficient for large result sets."
  },
  temp_btree: {
    color: "#e65100",
    bg: "#fff3e0",
    label: "TEMP B-TREE",
    tip: "A temporary B-tree is used for sorting (ORDER BY), grouping (GROUP BY), or deduplication (DISTINCT). Expected for these operations when no index covers the sort order."
  },
  compound: {
    color: "#616161",
    bg: "#f5f5f5",
    label: "COMPOUND",
    tip: "A compound query (UNION, UNION ALL, INTERSECT, or EXCEPT)."
  },
  other: {
    color: "#616161",
    bg: "#f5f5f5",
    label: "OTHER",
    tip: "Query plan operation."
  }
};

function buildPlanTree(rows: PlanRow[]): PlanTreeNode[] {
  const nodeMap = new Map<number, PlanTreeNode>();
  for (const row of rows) {
    nodeMap.set(row.id, { ...row, children: [], depth: 0 });
  }
  const roots: PlanTreeNode[] = [];
  for (const node of nodeMap.values()) {
    const parentNode = nodeMap.get(node.parent);
    if (parentNode && parentNode !== node) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  }
  function setDepth(nodes: PlanTreeNode[], depth: number) {
    for (const n of nodes) {
      n.depth = depth;
      setDepth(n.children, depth + 1);
    }
  }
  setDepth(roots, 0);
  return roots;
}

function PlanNodeRow({
  node,
  expanded,
  onToggle
}: {
  node: PlanTreeNode;
  expanded: boolean;
  onToggle: () => void;
}) {
  const op = classifyOperation(node.detail);
  const style = OP_STYLES[op];
  const hasChildren = node.children.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        pl: node.depth * 3,
        py: 0.4,
        "&:hover": { backgroundColor: "action.hover" },
        borderRadius: 0.5
      }}
    >
      <Box
        sx={{ width: 24, flexShrink: 0, cursor: hasChildren ? "pointer" : "default" }}
        onClick={hasChildren ? onToggle : undefined}
      >
        {hasChildren &&
          (expanded ? (
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          ))}
      </Box>
      <Tooltip title={style.tip} arrow placement="top-start">
        <Chip
          label={style.label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 700,
            color: style.color,
            backgroundColor: style.bg,
            mr: 1,
            cursor: "help",
            minWidth: 80
          }}
        />
      </Tooltip>
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "nowrap" }}
      >
        {node.detail}
      </Typography>
    </Box>
  );
}

function formatExplainText(rows: ExplainRow[]): string {
  return rows
    .map((r) => {
      const parts = [
        String(r.addr).padStart(4),
        r.opcode.padEnd(16),
        String(r.p1).padStart(4),
        String(r.p2).padStart(4),
        String(r.p3).padStart(4),
        (r.p4 || "").padEnd(20),
        r.p5 || "",
        r.comment ? `; ${r.comment}` : ""
      ];
      return parts.join("  ").trimEnd();
    })
    .join("\n");
}

function formatQueryPlanText(plan: PlanRow[]): string {
  const tree = buildPlanTree(plan);
  const lines: string[] = [];
  function walk(nodes: PlanTreeNode[], prefix: string, isLast: boolean[]) {
    nodes.forEach((node, i) => {
      const last = i === nodes.length - 1;
      const connector = prefix
        ? (last ? "`--" : "|--")
        : "";
      lines.push(`${prefix}${connector}${node.detail}`);
      const childPrefix = prefix + (prefix ? (last ? "   " : "|  ") : "");
      walk(node.children, childPrefix, [...isLast, last]);
    });
  }
  walk(tree, "", []);
  return lines.join("\n");
}

function QueryPlanExplorer({
  plan,
  explainRows,
  sql
}: {
  plan: PlanRow[];
  explainRows: ExplainRow[];
  sql: string;
}) {
  const tree = buildPlanTree(plan);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [activeTab, setActiveTab] = useState(0);

  const toggleNode = useCallback((id: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function renderNodes(nodes: PlanTreeNode[]): React.ReactNode[] {
    const result: React.ReactNode[] = [];
    for (const node of nodes) {
      const isExpanded = !collapsed.has(node.id);
      result.push(
        <PlanNodeRow
          key={node.id}
          node={node}
          expanded={isExpanded}
          onToggle={() => toggleNode(node.id)}
        />
      );
      if (isExpanded && node.children.length > 0) {
        result.push(...renderNodes(node.children));
      }
    }
    return result;
  }

  const scanCount = plan.filter(
    (r) => classifyOperation(r.detail) === "scan"
  ).length;
  const autoIndexCount = plan.filter(
    (r) => classifyOperation(r.detail) === "auto_index"
  ).length;
  const tempBtreeCount = plan.filter(
    (r) => classifyOperation(r.detail) === "temp_btree"
  ).length;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: "16px !important" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon />
            <Typography variant="h6">Query Plan</Typography>
            <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
              {scanCount > 0 && (
                <Chip
                  label={`${scanCount} SCAN${scanCount > 1 ? "s" : ""}`}
                  size="small"
                  sx={{ backgroundColor: "#ffebee", color: "#b71c1c", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                />
              )}
              {autoIndexCount > 0 && (
                <Chip
                  label={`${autoIndexCount} AUTO INDEX`}
                  size="small"
                  sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                />
              )}
              {tempBtreeCount > 0 && (
                <Chip
                  label={`${tempBtreeCount} TEMP B-TREE`}
                  size="small"
                  sx={{ backgroundColor: "#fff3e0", color: "#e65100", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                />
              )}
              {scanCount === 0 && autoIndexCount === 0 && tempBtreeCount === 0 && (
                <Chip
                  label="All indexed"
                  size="small"
                  sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 700, fontSize: "0.65rem", height: 20 }}
                />
              )}
            </Stack>
          </Stack>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ minHeight: 36, mb: 1.5, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label={`EXPLAIN QUERY PLAN (${plan.length})`}
            sx={{ minHeight: 36, textTransform: "none", fontSize: "0.8rem" }}
          />
          <Tab
            label={`EXPLAIN (${explainRows.length})`}
            sx={{ minHeight: 36, textTransform: "none", fontSize: "0.8rem" }}
          />
          <Tab
            label="SQL"
            sx={{ minHeight: 36, textTransform: "none", fontSize: "0.8rem" }}
          />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Copy query plan">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(formatQueryPlanText(plan))}
                sx={{ position: "absolute", top: 4, right: 20, zIndex: 1 }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Paper
              variant="outlined"
              sx={{ p: 1.5, maxHeight: 500, overflowY: "auto" }}
            >
              {renderNodes(tree)}
            </Paper>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Copy bytecodes">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(
                  `addr  opcode            p1    p2    p3    p4                    p5  comment\n${"─".repeat(90)}\n${formatExplainText(explainRows)}`
                )}
                sx={{ position: "absolute", top: 4, right: 20, zIndex: 3 }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Paper variant="outlined" sx={{ maxHeight: 500, overflowY: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>Addr</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>Opcode</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }} align="right">P1</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }} align="right">P2</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }} align="right">P3</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>P4</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.7rem", py: 0.5 }}>Comment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {explainRows.map((row) => (
                    <TableRow key={row.addr} hover>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }}>{row.addr}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3, fontWeight: 600 }}>{row.opcode}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }} align="right">{row.p1}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }} align="right">{row.p2}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3 }} align="right">{row.p3}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem", py: 0.3, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{row.p4}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.65rem", py: 0.3, color: "text.secondary" }}>{row.comment}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ position: "relative" }}>
            <Tooltip title="Copy SQL">
              <IconButton
                size="small"
                onClick={() => navigator.clipboard.writeText(sql)}
                sx={{ position: "absolute", top: 4, right: 20, zIndex: 1, color: "#999" }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Box
              component="pre"
              sx={{
                fontSize: "0.7rem",
                fontFamily: "monospace",
                backgroundColor: "#1e1e1e",
                color: "#d4d4d4",
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                maxHeight: 500,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all"
              }}
            >
              {sql}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export const AssessmentHistoryDebug = () => {
  // Program selection
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<ProgramOption | null>(
    null
  );

  // User selection
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(
    null
  );

  // For "All Assessments" mode, optionally filter by subject
  const [selectedSubject, setSelectedSubject] = useState<MemberOption | null>(
    null
  );

  // Query parameters
  const [mode, setMode] = useState<"my" | "all">("my");
  const [includeSelfAssessments, setIncludeSelfAssessments] = useState(true);

  // Results
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [history, setHistory] = useState<RunResult[]>([]);

  // Benchmark
  const [benchmarkIterations, setBenchmarkIterations] = useState(5);
  const [benchmarking, setBenchmarking] = useState(false);
  const [benchmarkProgress, setBenchmarkProgress] = useState(0);
  const [benchmarkResult, setBenchmarkResult] =
    useState<BenchmarkResult | null>(null);
  const benchmarkAbortRef = useRef(false);

  // EXPLAIN QUERY PLAN
  const [queryPlan, setQueryPlan] = useState<PlanRow[] | null>(null);
  const [explainRows, setExplainRows] = useState<ExplainRow[]>([]);
  const [queryPlanSql, setQueryPlanSql] = useState<string>("");
  const [queryPlanLoading, setQueryPlanLoading] = useState(false);

  // Right panel tabs
  const [rightTab, setRightTab] = useState(0);
  const [resultsSubTab, setResultsSubTab] = useState(0);

  // Live timer — active during both single runs and benchmarks
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerStartRef = useRef<number>(0);
  const timerActive = loading || benchmarking;

  useEffect(() => {
    if (!timerActive) return;
    timerStartRef.current = performance.now();
    setElapsedMs(0);
    const interval = setInterval(() => {
      setElapsedMs(Math.round(performance.now() - timerStartRef.current));
    }, 53);
    return () => clearInterval(interval);
  }, [timerActive]);

  const resultRef = useRef<HTMLDivElement>(null);
  const benchmarkRef = useRef<HTMLDivElement>(null);

  // Load programs on first interaction
  const loadPrograms = useCallback(async () => {
    if (programs.length > 0) return;
    setProgramsLoading(true);
    try {
      const resp = await API.get("simpl-api", "programs", {
        queryStringParameters: { limit: "500" }
      });
      const programList = (resp.programs || [])
        .map((p: any) => ({
          id: p.id,
          name: p.name || "Unnamed",
          institutionName: p.institution?.name
        }))
        .sort((a: ProgramOption, b: ProgramOption) =>
          a.name.localeCompare(b.name)
        );
      setPrograms(programList);
    } catch (err) {
      console.error("Failed to load programs:", err);
    } finally {
      setProgramsLoading(false);
    }
  }, [programs.length]);

  // Load members when a program is selected
  const loadMembers = useCallback(async (programId: string) => {
    setMembersLoading(true);
    setMembers([]);
    setSelectedMember(null);
    setSelectedSubject(null);
    try {
      const resp = await API.get("simpl-api", "program-memberships/search", {
        queryStringParameters: {
          filter: JSON.stringify({
            programId: { eq: programId },
            endDate: { activeOnly: true }
          })
        }
      });
      const memberList = (resp.programmemberships || []).map((m: any) => ({
        userId: m.programMember?.id || m.programMemberId,
        fullName:
          `${m.programMember?.firstName || ""} ${m.programMember?.lastName || ""}`.trim() ||
          "Unknown",
        email: m.programMember?.email || "",
        roleName: m.programRole || "Unknown",
        membershipId: m.id
      }));
      memberList.sort((a: MemberOption, b: MemberOption) => {
        const roleOrder: Record<string, number> = {
          Trainee: 0,
          Attending: 1
        };
        const ra = roleOrder[a.roleName] ?? 2;
        const rb = roleOrder[b.roleName] ?? 2;
        if (ra !== rb) return ra - rb;
        return a.fullName.localeCompare(b.fullName);
      });
      setMembers(memberList);
    } catch (err) {
      console.error("Failed to load members:", err);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const buildQueryParams = useCallback(() => {
    if (!selectedMember) return null;
    const params: Record<string, string> = {
      loggedInUserId: selectedMember.userId,
      membersAssessmentsOnly: mode === "my" ? "true" : "false",
      includeSelfAssessments: includeSelfAssessments ? "true" : "false"
    };
    if (mode === "all" && selectedSubject) {
      params.selectedUserId = selectedSubject.userId;
    }
    return params;
  }, [selectedMember, mode, includeSelfAssessments, selectedSubject]);

  const executeQuery = useCallback(
    async (
      token: string
    ): Promise<{ data: any; timing: number; eventCount: number; hash: string }> => {
      const params = buildQueryParams();
      if (!params) throw new Error("No query params");
      const base =
        process.env.REACT_APP_API_BASE_URL ||
        `http://${window.location.hostname}:3001`;
      const queryString = new URLSearchParams(params).toString();
      const url = `${base}/api/assessment/history?${queryString}`;

      const startTime = performance.now();
      const resp = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      const timing = Math.round(performance.now() - startTime);

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${text}`);
      }

      const data = await resp.json();
      const eventCount = data.assessmentHistoryEvents?.length || 0;
      const stableJson = JSON.stringify(
        data.assessmentHistoryEvents?.map((e: any) => e.id).sort()
      );
      const hash = hashString(stableJson);
      return { data, timing, eventCount, hash };
    },
    [buildQueryParams]
  );

  const runQuery = useCallback(async () => {
    if (!selectedMember) return;
    setLoading(true);
    setError(null);

    try {
      const token = await getCognitoJWT();
      const { data, timing, eventCount, hash } = await executeQuery(token);

      const runResult: RunResult = {
        data,
        timing,
        eventCount,
        hash,
        timestamp: new Date().toISOString()
      };

      setResult(runResult);
      setHistory((prev) => [runResult, ...prev].slice(0, 20));
      setRightTab(1);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [selectedMember, executeQuery]);

  const runExplain = useCallback(async () => {
    if (!selectedMember) return;
    setQueryPlanLoading(true);
    setError(null);

    try {
      const token = await getCognitoJWT();
      const params = buildQueryParams();
      if (!params) throw new Error("No query params");

      const base =
        process.env.REACT_APP_API_BASE_URL ||
        `http://${window.location.hostname}:3001`;
      const queryString = new URLSearchParams({
        ...params,
        explain: "true"
      }).toString();
      const url = `${base}/api/assessment/history?${queryString}`;

      const resp = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      setQueryPlan(data.queryPlan || []);
      setExplainRows(data.explain || []);
      setQueryPlanSql(data.sql || "");
      setRightTab(0);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setQueryPlanLoading(false);
    }
  }, [selectedMember, buildQueryParams]);

  const runBenchmark = useCallback(async () => {
    if (!selectedMember || !selectedProgram) return;
    setBenchmarking(true);
    setBenchmarkProgress(0);
    setBenchmarkResult(null);
    benchmarkAbortRef.current = false;
    setError(null);

    const runs: BenchmarkRun[] = [];
    try {
      const token = await getCognitoJWT();

      for (let i = 0; i < benchmarkIterations; i++) {
        if (benchmarkAbortRef.current) break;
        setBenchmarkProgress(i + 1);
        try {
          const { timing, eventCount, hash } = await executeQuery(token);
          runs.push({ iteration: i + 1, timing, eventCount, hash });
        } catch (err: any) {
          runs.push({
            iteration: i + 1,
            timing: 0,
            eventCount: 0,
            hash: "error",
            error: err.message || String(err)
          });
        }
      }

      const successRuns = runs.filter((r) => !r.error);
      const timings = successRuns.map((r) => r.timing);
      const hashes = new Set(successRuns.map((r) => r.hash));
      const stats = computeStats(timings);

      const result: BenchmarkResult = {
        runs,
        stats: {
          count: runs.length,
          successCount: successRuns.length,
          ...stats,
          allHashesMatch: hashes.size <= 1
        },
        params: {
          user: selectedMember.fullName,
          userId: selectedMember.userId,
          program: selectedProgram.name,
          mode,
          iterations: benchmarkIterations,
          timestamp: new Date().toISOString()
        }
      };

      setBenchmarkResult(result);
      setRightTab(2);
    } catch (err: any) {
      setError(err.message || String(err));
    } finally {
      setBenchmarking(false);
    }
  }, [
    selectedMember,
    selectedProgram,
    benchmarkIterations,
    mode,
    executeQuery
  ]);

  const exportBenchmark = useCallback(() => {
    if (!benchmarkResult) return;
    const blob = new Blob([JSON.stringify(benchmarkResult, null, 2)], {
      type: "application/json"
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const ts = benchmarkResult.params.timestamp
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    a.download = `benchmark-${ts}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [benchmarkResult]);

  const copyJson = useCallback(() => {
    if (result?.data) {
      navigator.clipboard.writeText(JSON.stringify(result.data, null, 2));
    }
  }, [result]);

  const traineeMembers = members.filter((m) => m.roleName === "Trainee");
  const subjectOptions =
    mode === "all" ? traineeMembers : [];

  return (
    <PageWrapper
      className="AssessmentHistoryDebug"
      headerText="Assessment History Debug"
    >
      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to="/debug/queries?query=assessment-history"
          >
            Open Query Explorer
          </Button>
        }
      >
        Try the new <strong>Debug: Query Explorer</strong> for a generalized query debugging experience across all queries.
      </Alert>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {/* ── Left Column: Controls ── */}
        <Card
          sx={{
            width: 380,
            minWidth: 380,
            position: "sticky",
            top: 16,
            mt: 2,
            maxHeight: "calc(100vh - 100px)",
            overflowY: "auto"
          }}
        >
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Query Parameters
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                size="small"
                options={programs}
                loading={programsLoading}
                getOptionLabel={(opt) =>
                  opt.institutionName
                    ? `${opt.name} (${opt.institutionName})`
                    : opt.name
                }
                value={selectedProgram}
                onOpen={loadPrograms}
                onChange={(_, val) => {
                  setSelectedProgram(val);
                  setResult(null);
                  if (val) {
                    loadMembers(val.id);
                  } else {
                    setMembers([]);
                    setSelectedMember(null);
                    setSelectedSubject(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Program"
                    placeholder="Search programs..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {programsLoading && (
                            <CircularProgress size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />

              <Autocomplete
                size="small"
                options={members}
                loading={membersLoading}
                disabled={!selectedProgram}
                groupBy={(option) => option.roleName}
                getOptionLabel={(opt) =>
                  `${opt.fullName} (${opt.roleName})`
                }
                value={selectedMember}
                onChange={(_, val) => {
                  setSelectedMember(val);
                  setResult(null);
                  setSelectedSubject(null);
                  if (val?.roleName === "Trainee") {
                    setMode("my");
                    setIncludeSelfAssessments(true);
                  } else if (val?.roleName === "Attending") {
                    setMode("my");
                    setIncludeSelfAssessments(false);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Impersonate User"
                    placeholder="Search members..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {membersLoading && (
                            <CircularProgress size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.membershipId}>
                    <Stack>
                      <Typography variant="body2">
                        {option.fullName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email} &middot;{" "}
                        <Chip
                          label={option.roleName}
                          size="small"
                          color={
                            option.roleName === "Trainee"
                              ? "primary"
                              : "secondary"
                          }
                          sx={{ height: 18, fontSize: "0.7rem" }}
                        />
                      </Typography>
                    </Stack>
                  </li>
                )}
              />

              {selectedMember && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    {selectedMember.userId}
                  </Typography>
                  <Chip
                    label={selectedMember.roleName}
                    color={
                      selectedMember.roleName === "Trainee"
                        ? "primary"
                        : "secondary"
                    }
                    size="small"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </Stack>
              )}

              {selectedMember && (
                <Box>
                  <RadioGroup
                    value={mode}
                    onChange={(e) => {
                      setMode(e.target.value as "my" | "all");
                      setResult(null);
                      setSelectedSubject(null);
                      setIncludeSelfAssessments(e.target.value === "my");
                    }}
                  >
                    <FormControlLabel
                      value="my"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">My Assessments</Typography>}
                    />
                    <FormControlLabel
                      value="all"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">All Assessments</Typography>}
                      disabled={selectedMember.roleName !== "Attending"}
                    />
                  </RadioGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeSelfAssessments}
                        onChange={(e) =>
                          setIncludeSelfAssessments(e.target.checked)
                        }
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Include Self Assessments</Typography>}
                    sx={{ ml: 0 }}
                  />
                </Box>
              )}

              {mode === "all" && selectedMember && (
                <Autocomplete
                  size="small"
                  options={subjectOptions}
                  getOptionLabel={(opt) =>
                    `${opt.fullName} (${opt.roleName})`
                  }
                  value={selectedSubject}
                  onChange={(_, val) => {
                    setSelectedSubject(val);
                    setResult(null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subject User (selectedUserId)"
                      placeholder="Search trainees..."
                    />
                  )}
                />
              )}

              <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
                <Stack spacing={1.5}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={
                      queryPlanLoading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <AccountTreeIcon />
                      )
                    }
                    onClick={runExplain}
                    disabled={
                      queryPlanLoading ||
                      loading ||
                      benchmarking ||
                      !selectedMember ||
                      (mode === "all" && !selectedSubject)
                    }
                  >
                    {queryPlanLoading ? "Explaining..." : "Explain"}
                  </Button>

                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayArrowIcon />
                      )
                    }
                    onClick={runQuery}
                    disabled={
                      loading ||
                      benchmarking ||
                      !selectedMember ||
                      (mode === "all" && !selectedSubject)
                    }
                  >
                    {loading ? (
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                        Running...
                        <Chip
                          label={
                            elapsedMs < 1000
                              ? `${elapsedMs}ms`
                              : elapsedMs < 10000
                                ? `${(elapsedMs / 1000).toFixed(1)}s`
                                : `${Math.round(elapsedMs / 1000)}s`
                          }
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            fontFamily: "monospace",
                            fontWeight: "bold",
                            backgroundColor: elapsedMs < 3000 ? "#e3f2fd" : elapsedMs < 10000 ? "#fff3e0" : "#ffebee",
                            color: elapsedMs < 3000 ? "#1565c0" : elapsedMs < 10000 ? "#e65100" : "#b71c1c"
                          }}
                        />
                      </Box>
                    ) : "Run Query"}
                  </Button>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {benchmarking ? (
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<StopIcon />}
                        onClick={() => {
                          benchmarkAbortRef.current = true;
                        }}
                      >
                        Stop ({benchmarkProgress}/{benchmarkIterations})
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<SpeedIcon />}
                        onClick={runBenchmark}
                        disabled={
                          loading ||
                          !selectedMember ||
                          (mode === "all" && !selectedSubject)
                        }
                      >
                        Benchmark
                      </Button>
                    )}
                    <TextField
                      type="number"
                      size="small"
                      value={benchmarkIterations}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (v > 0 && v <= 50) setBenchmarkIterations(v);
                      }}
                      disabled={benchmarking}
                      inputProps={{ min: 1, max: 50, style: { width: 40 } }}
                      sx={{ width: 80, minWidth: 80 }}
                      label="Runs"
                    />
                  </Stack>

                  {benchmarking && (
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={(benchmarkProgress / benchmarkIterations) * 100}
                        sx={{ height: 6, borderRadius: 1 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        Iteration {benchmarkProgress} of {benchmarkIterations}
                      </Typography>
                    </Box>
                  )}

                </Stack>
              </Box>
            </Stack>

          </CardContent>
        </Card>

        {/* ── Right Column: Tabbed panels ── */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Tabs
            value={rightTab}
            onChange={(_, v) => setRightTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}
          >
            <Tab
              icon={<AccountTreeIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Query Plan"
              sx={{ textTransform: "none", minHeight: 42 }}
            />
            <Tab
              label={result ? `Results (${result.eventCount})` : "Results"}
              sx={{ textTransform: "none", minHeight: 42 }}
            />
            <Tab
              icon={<SpeedIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Benchmark"
              sx={{ textTransform: "none", minHeight: 42 }}
            />
          </Tabs>

          {/* ── Query Plan tab ── */}
          {rightTab === 0 && (
            <Box sx={{ pt: 2 }}>
              {queryPlan ? (
                <QueryPlanExplorer plan={queryPlan} explainRows={explainRows} sql={queryPlanSql} />
              ) : (
                <Alert severity="info">
                  Click "Explain" to generate the query plan.
                </Alert>
              )}
            </Box>
          )}

          {/* ── Results tab ── */}
          {rightTab === 1 && (
            <Box sx={{ pt: 0 }}>
              {result ? (
                <>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ py: 1 }}
                  >
                    <Chip
                      label={`${result.timing.toLocaleString()}ms`}
                      color={
                        result.timing < 1000
                          ? "success"
                          : result.timing < 3000
                            ? "warning"
                            : "error"
                      }
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={result.hash}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {result.eventCount} events
                    </Typography>
                  </Stack>

                  <Tabs
                    value={resultsSubTab}
                    onChange={(_, v) => setResultsSubTab(v)}
                    sx={{ minHeight: 32, borderBottom: 1, borderColor: "divider", mb: 1.5 }}
                  >
                    <Tab label="Table" sx={{ minHeight: 32, textTransform: "none", fontSize: "0.8rem" }} />
                    <Tab label="Raw JSON" sx={{ minHeight: 32, textTransform: "none", fontSize: "0.8rem" }} />
                    {history.length > 1 && (
                      <Tab label={`Run History (${history.length})`} sx={{ minHeight: 32, textTransform: "none", fontSize: "0.8rem" }} />
                    )}
                  </Tabs>

                  {resultsSubTab === 0 && result.data.assessmentHistoryEvents?.length > 0 && (
                    <Box sx={{ position: "relative" }} ref={resultRef}>
                      <Tooltip title="Copy as CSV">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const events = result.data.assessmentHistoryEvents;
                            const header = "Date,Activity,Subject,Rater,Answers,Self,Event ID";
                            const csvEscape = (v: string) => v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
                            const rows = events.map((e: any) => [
                              e.activityEventDate ? new Date(e.activityEventDate).toLocaleDateString() : "N/A",
                              csvEscape(e.activity?.title || "N/A"),
                              csvEscape(e.subjectUser?.fullName || "N/A"),
                              csvEscape(e.raterUser?.fullName || "(hidden)"),
                              String(e.answerEvents?.length || 0),
                              e.selfAssessmentEvent ? "Yes" : "No",
                              e.id || ""
                            ].join(","));
                            navigator.clipboard.writeText([header, ...rows].join("\n"));
                          }}
                          sx={{ position: "absolute", top: 4, right: 20, zIndex: 3 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <TableContainer
                        component={Paper}
                        sx={{ maxHeight: "65vh" }}
                      >
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Activity</TableCell>
                              <TableCell>Subject</TableCell>
                              <TableCell>Rater</TableCell>
                              <TableCell>Answers</TableCell>
                              <TableCell>Self?</TableCell>
                              <TableCell>Event ID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {result.data.assessmentHistoryEvents.map(
                              (event: any, idx: number) => (
                                <TableRow key={event.id || idx} hover>
                                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                                    {event.activityEventDate
                                      ? new Date(
                                          event.activityEventDate
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title={event.activity?.id || ""}>
                                      <span>
                                        {event.activity?.title || "N/A"}
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell>
                                    <Stack>
                                      <Typography variant="body2">
                                        {event.subjectUser?.fullName || "N/A"}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {event.subjectUser
                                          ?.sharedProgramsDisplay || ""}
                                      </Typography>
                                    </Stack>
                                  </TableCell>
                                  <TableCell>
                                    {event.raterUser ? (
                                      <Stack>
                                        <Typography variant="body2">
                                          {event.raterUser.fullName}
                                          {event.raterUser.isLoggedInUser && (
                                            <Chip
                                              label="You"
                                              size="small"
                                              sx={{
                                                ml: 0.5,
                                                height: 16,
                                                fontSize: "0.65rem"
                                              }}
                                            />
                                          )}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {event.raterUser
                                            .sharedProgramsDisplay || ""}
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        (hidden)
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {event.answerEvents?.length || 0}
                                  </TableCell>
                                  <TableCell>
                                    {event.selfAssessmentEvent ? (
                                      <Chip
                                        label="Yes"
                                        size="small"
                                        color="info"
                                        sx={{ height: 18, fontSize: "0.7rem" }}
                                      />
                                    ) : (
                                      "—"
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontFamily: "monospace" }}
                                    >
                                      {event.id?.slice(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {resultsSubTab === 1 && (
                    <Box sx={{ position: "relative" }}>
                      <Tooltip title="Copy JSON">
                        <IconButton
                          size="small"
                          onClick={copyJson}
                          sx={{ position: "absolute", top: 4, right: 20, zIndex: 1, color: "#999" }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Box
                        component="pre"
                        sx={{
                          overflow: "auto",
                          maxHeight: "65vh",
                          fontSize: "0.75rem",
                          backgroundColor: "#f5f5f5",
                          p: 2,
                          borderRadius: 1
                        }}
                      >
                        {JSON.stringify(result.data, null, 2)}
                      </Box>
                    </Box>
                  )}

                  {resultsSubTab === 2 && history.length > 1 && (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Matching hashes confirm identical assessment event IDs across
                        runs.
                      </Typography>
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Time</TableCell>
                              <TableCell>Response (ms)</TableCell>
                              <TableCell>Events</TableCell>
                              <TableCell>Hash</TableCell>
                              <TableCell>Match?</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {history.map((run, idx) => (
                              <TableRow key={run.timestamp}>
                                <TableCell>{history.length - idx}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {new Date(run.timestamp).toLocaleTimeString()}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={`${run.timing}ms`}
                                    size="small"
                                    color={
                                      run.timing < 1000
                                        ? "success"
                                        : run.timing < 3000
                                          ? "warning"
                                          : "error"
                                    }
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{run.eventCount}</TableCell>
                                <TableCell>
                                  <code>{run.hash}</code>
                                </TableCell>
                                <TableCell>
                                  {idx < history.length - 1 ? (
                                    run.hash ===
                                    history[history.length - 1].hash ? (
                                      <Chip
                                        label="Match"
                                        size="small"
                                        color="success"
                                      />
                                    ) : (
                                      <Chip
                                        label="Different"
                                        size="small"
                                        color="error"
                                      />
                                    )
                                  ) : (
                                    <Chip
                                      label="Baseline"
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              ) : (
                <Box sx={{ pt: 2 }}>
                  <Alert severity="info">
                    Run a query to see results.
                  </Alert>
                </Box>
              )}
            </Box>
          )}

          {/* ── Benchmark tab ── */}
          {rightTab === 2 && (
            <Box sx={{ pt: 2 }}>
              {benchmarkResult ? (
                <div ref={benchmarkRef}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="h6">
                          Benchmark Results
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={exportBenchmark}
                        >
                          Export JSON
                        </Button>
                      </Stack>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {benchmarkResult.params.user} ({benchmarkResult.params.mode}{" "}
                        mode) in {benchmarkResult.params.program} &mdash;{" "}
                        {new Date(benchmarkResult.params.timestamp).toLocaleString()}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                        <Chip
                          label={`Median: ${benchmarkResult.stats.median}ms`}
                          color="primary"
                          variant="filled"
                        />
                        <Chip
                          label={`Mean: ${benchmarkResult.stats.mean}ms`}
                          variant="outlined"
                        />
                        <Chip
                          label={`Min: ${benchmarkResult.stats.min}ms`}
                          color="success"
                          variant="outlined"
                        />
                        <Chip
                          label={`Max: ${benchmarkResult.stats.max}ms`}
                          color="error"
                          variant="outlined"
                        />
                        <Chip
                          label={`P95: ${benchmarkResult.stats.p95}ms`}
                          variant="outlined"
                        />
                        <Chip
                          label={`StdDev: ${benchmarkResult.stats.stdDev}ms`}
                          variant="outlined"
                        />
                        <Chip
                          label={`${benchmarkResult.stats.successCount}/${benchmarkResult.stats.count} succeeded`}
                          color={
                            benchmarkResult.stats.successCount ===
                            benchmarkResult.stats.count
                              ? "success"
                              : "warning"
                          }
                          variant="outlined"
                        />
                        <Chip
                          label={
                            benchmarkResult.stats.allHashesMatch
                              ? "All hashes match"
                              : "Hash mismatch!"
                          }
                          color={
                            benchmarkResult.stats.allHashesMatch
                              ? "success"
                              : "error"
                          }
                          variant={
                            benchmarkResult.stats.allHashesMatch
                              ? "outlined"
                              : "filled"
                          }
                        />
                      </Stack>

                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Time (ms)</TableCell>
                              <TableCell>Events</TableCell>
                              <TableCell>Hash</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {benchmarkResult.runs.map((run) => (
                              <TableRow
                                key={run.iteration}
                                sx={
                                  run.error
                                    ? {
                                        backgroundColor: "error.light",
                                        opacity: 0.7
                                      }
                                    : undefined
                                }
                              >
                                <TableCell>{run.iteration}</TableCell>
                                <TableCell>
                                  {run.error ? (
                                    "—"
                                  ) : (
                                    <Chip
                                      label={`${run.timing}ms`}
                                      size="small"
                                      color={
                                        run.timing < 1000
                                          ? "success"
                                          : run.timing < 3000
                                            ? "warning"
                                            : "error"
                                      }
                                      variant="outlined"
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  {run.error ? "—" : run.eventCount}
                                </TableCell>
                                <TableCell>
                                  <code>{run.hash}</code>
                                </TableCell>
                                <TableCell>
                                  {run.error ? (
                                    <Tooltip title={run.error}>
                                      <Chip
                                        label="Error"
                                        size="small"
                                        color="error"
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Chip
                                      label="OK"
                                      size="small"
                                      color="success"
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Alert severity="info">
                  Run a benchmark to see results.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </PageWrapper>
  );
};
